/**
 * Thin fetch wrapper with JWT injection, automatic refresh on 401, and
 * envelope-aware error handling. All endpoints return either a typed payload
 * or throw `ApiClientError` which the UI can render.
 */

import type {
  ApiError,
  AuthTokens,
  Booking,
  MasterApplication,
  MasterSettings,
  Service,
  SlotsList,
  User,
  WaitlistEntry,
  WeeklySchedule,
  WeeklyScheduleSlot,
  Master,
} from './types';
import { getInitData } from './telegram';

const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const ACCESS_KEY = 'nailbot.access';
const REFRESH_KEY = 'nailbot.refresh';

export class ApiClientError extends Error {
  status: number;
  type: string;

  constructor(message: string, status: number, type: string) {
    super(message);
    this.status = status;
    this.type = type;
  }
}

export const tokenStore = {
  get access(): string | null {
    return sessionStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return sessionStorage.getItem(REFRESH_KEY);
  },
  set(tokens: { access_token: string; refresh_token: string }): void {
    sessionStorage.setItem(ACCESS_KEY, tokens.access_token);
    sessionStorage.setItem(REFRESH_KEY, tokens.refresh_token);
  },
  clear(): void {
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
  },
};

export interface ServiceWritePayload {
  name: string;
  description?: string | null;
  price_min: number;
  price_max?: number | null;
  duration_minutes: number;
}

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  skipAuth?: boolean;
}

async function rawFetch<T>(
  path: string,
  options: FetchOptions = {},
  retried = false,
): Promise<T> {
  const url = new URL(API_BASE + path, window.location.origin);
  if (options.query) {
    for (const [k, v] of Object.entries(options.query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  if (!options.skipAuth) {
    const token = tokenStore.access;
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (response.status === 401 && !options.skipAuth && !retried) {
    const ok = await tryRefresh();
    if (ok) return rawFetch<T>(path, options, true);
    tokenStore.clear();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const data: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const err = data as ApiError;
    throw new ApiClientError(
      err?.error?.message ?? 'Запрос не удался',
      response.status,
      err?.error?.type ?? 'error',
    );
  }
  return data as T;
}

async function tryRefresh(): Promise<boolean> {
  const refresh = tokenStore.refresh;
  if (!refresh) return false;
  try {
    const tokens = await rawFetch<AuthTokens>(
      '/auth/refresh',
      { method: 'POST', body: { refresh_token: refresh }, skipAuth: true },
      true,
    );
    tokenStore.set(tokens);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const auth = {
  async loginWithInitData(initData?: string): Promise<AuthTokens> {
    const data = initData ?? getInitData();
    if (!data) {
      throw new ApiClientError(
        'initData не получен от Telegram. Откройте Mini App из бота.',
        400,
        'badRequest',
      );
    }
    const tokens = await rawFetch<AuthTokens>('/auth/validate-init-data', {
      method: 'POST',
      body: { init_data: data },
      skipAuth: true,
    });
    tokenStore.set(tokens);
    return tokens;
  },
  logout(): void {
    tokenStore.clear();
  },
};

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const api = {
  users: {
    me: () => rawFetch<User>('/users/me'),
    update: (body: Partial<Pick<User, 'first_name' | 'last_name' | 'phone' | 'language_code'>>) =>
      rawFetch<User>('/users/me', { method: 'PUT', body }),
    delete: () => rawFetch<void>('/users/me', { method: 'DELETE' }),
  },
  services: {
    listPublic: () => rawFetch<Service[]>('/services'),
    listAdmin: () => rawFetch<Service[]>('/services/all'),
    get: (id: string) => rawFetch<Service>(`/services/${id}`),
    create: (body: ServiceWritePayload) =>
      rawFetch<Service>('/services', { method: 'POST', body }),
    update: (id: string, body: Partial<ServiceWritePayload>) =>
      rawFetch<Service>(`/services/${id}`, { method: 'PUT', body }),
    deactivate: (id: string) =>
      rawFetch<Service>(`/services/${id}/deactivate`, { method: 'PATCH' }),
    activate: (id: string) =>
      rawFetch<Service>(`/services/${id}/activate`, { method: 'PATCH' }),
  },
  masters: {
    list: () => rawFetch<Master[]>('/masters'),
    schedule: (masterId: string) =>
      rawFetch<WeeklySchedule>(`/masters/${masterId}/schedule`),
    updateSchedule: (masterId: string, slots: WeeklyScheduleSlot[]) =>
      rawFetch<WeeklySchedule>(`/masters/${masterId}/schedule`, {
        method: 'PUT',
        body: { slots },
      }),
    settings: (masterId: string) =>
      rawFetch<MasterSettings>(`/masters/${masterId}/settings`),
    updateSettings: (masterId: string, body: Partial<MasterSettings>) =>
      rawFetch<MasterSettings>(`/masters/${masterId}/settings`, {
        method: 'PUT',
        body,
      }),
    addException: (
      masterId: string,
      body: { exception_date: string; is_day_off?: boolean; reason?: string },
    ) =>
      rawFetch<unknown>(`/masters/${masterId}/exceptions`, {
        method: 'POST',
        body: { is_day_off: true, ...body },
      }),
  },
  slots: {
    list: (params: { master_id: string; service_id: string; date: string }) =>
      rawFetch<SlotsList>('/slots', { query: params }),
    dates: (params: { master_id: string; service_id: string; days_ahead?: number }) =>
      rawFetch<string[]>('/slots/dates', { query: params }),
  },
  bookings: {
    create: (body: {
      master_id: string;
      service_id: string;
      slot_start: string;
      client_note?: string;
      idempotency_key?: string;
    }) => rawFetch<Booking>('/bookings', { method: 'POST', body }),
    my: (includeTerminal = true) =>
      rawFetch<Booking[]>('/bookings/my', {
        query: { include_terminal: includeTerminal },
      }),
    forMaster: (includeTerminal = false) =>
      rawFetch<Booking[]>('/bookings/master', {
        query: { include_terminal: includeTerminal },
      }),
    get: (id: string) => rawFetch<Booking>(`/bookings/${id}`),
    confirm: (id: string, comment?: string) =>
      rawFetch<Booking>(`/bookings/${id}/confirm`, {
        method: 'PATCH',
        body: { comment },
      }),
    reject: (id: string, comment?: string) =>
      rawFetch<Booking>(`/bookings/${id}/reject`, {
        method: 'PATCH',
        body: { comment },
      }),
    tentative: (id: string, comment?: string) =>
      rawFetch<Booking>(`/bookings/${id}/tentative`, {
        method: 'PATCH',
        body: { comment },
      }),
    cancel: (id: string, comment?: string) =>
      rawFetch<Booking>(`/bookings/${id}/cancel`, {
        method: 'PATCH',
        body: { comment },
      }),
    complete: (id: string, comment?: string) =>
      rawFetch<Booking>(`/bookings/${id}/complete`, {
        method: 'PATCH',
        body: { comment },
      }),
  },
  waitlist: {
    list: () => rawFetch<WaitlistEntry[]>('/waitlist/my'),
    subscribe: (body: {
      master_id: string;
      service_id?: string;
      desired_date?: string;
      ttl_days?: number;
    }) => rawFetch<WaitlistEntry>('/waitlist', { method: 'POST', body }),
    unsubscribe: (id: string) =>
      rawFetch<void>(`/waitlist/${id}`, { method: 'DELETE' }),
  },
  admin: {
    pendingMasters: () =>
      rawFetch<MasterApplication[]>('/admin/pending-masters'),
    submitMasterApp: (body: {
      full_name: string;
      phone: string;
      experience?: string;
      about?: string;
    }) => rawFetch<MasterApplication>('/admin/master-applications', { method: 'POST', body }),
    approveMaster: (id: string) =>
      rawFetch<MasterApplication>(`/admin/master-applications/${id}/approve`, {
        method: 'PATCH',
      }),
    rejectMaster: (id: string) =>
      rawFetch<MasterApplication>(`/admin/master-applications/${id}/reject`, {
        method: 'PATCH',
      }),
  },
};
